import type { Translation } from "./pt-BR";

export const en: Translation = {
  common: {
    appName: "Let's Go Run",
    loading: "Loading...",
    help: "Need help?",
    language: "Language",
    logout: "Sign out",
  },
  footer: {
    developedBy: "DEVELOPED BY",
    legalNav: "Legal documents",
    privacy: "Privacy",
    terms: "Terms of Use",
    linkedin: "Saulo Rangel on LinkedIn",
    github: "Saulo Rangel on GitHub",
    email: "Email Saulo Rangel",
  },
  auth: {
    photoAlt: "",
    heroTitle1: "Your pace, your plan",
    heroTitle2: "Every kilometer takes you further.",
    heroText: "Plans that adapt to your routine, your body and your next race: from your first 5K to the marathon.",
    plansLabel: "Plans for every distance",
    tabs: "Account access",
    tabLogin: "Sign in",
    tabRegister: "Create account",
    welcome: "Good to see you again 👋",
    welcomeText: "Sign in to see today's workout and keep following your plan.",
    privacyNote: "Your training and health data travel encrypted, are kept in encrypted backups and are never sold.",
    email: "Email",
    emailPlaceholder: "you@example.com",
    password: "Password",
    passwordPlaceholder: "Enter your password",
    forgot: "Forgot my password",
    rememberMe: "Keep me signed in on this device",
    signIn: "Sign in",
    signingIn: "Signing in...",
    noAccount: "Not running with us yet?",
    createFree: "Create a free account",
    registerTitle: "Create your account",
    name: "Full name",
    namePlaceholder: "What should we call you?",
    newPasswordPlaceholder: "At least 10 characters",
    passwordHint: "At least 10 characters. Avoid common passwords and parts of your email.",
    adult: "I declare that I am 18 years old or older.",
    acceptTerms: "I have read and accept the <terms>Terms of Use</terms> and the <privacy>Privacy Policy</privacy>.",
    acceptHealthData: "I authorize the processing of my health data (required for the workouts).",
    createAccount: "Create my account",
    creating: "Creating account...",
    registerDone: "We sent you an email to confirm your registration. Open the link to activate the account.",
    backToLogin: "Back to sign in",
    forgotTitle: "Forgot your password?",
    forgotText: "No problem. Enter your account email and we will send you a link to create a new password.",
    forgotSubmit: "Send link",
    forgotSending: "Sending...",
    forgotNote: "The link is valid for 60 minutes and can only be used once.",
    sentTitle: "Check your email",
    sentText: "If an account exists with that email, we sent a link to create a new password. It is valid for 60 minutes.",
    sentSpam: "Didn't arrive? Check your spam folder or try again in a few minutes.",
    sentOther: "Send to another email",
    resetTitle: "Create a new password",
    resetText: "Choose a password with at least 10 characters. Avoid common passwords and parts of your email.",
    newPassword: "New password",
    confirmPassword: "Confirm new password",
    repeatPlaceholder: "Repeat the password",
    resetNote: "When you save, you are signed out of all devices and sign in again with the new password.",
    resetSubmit: "Save new password",
    resetSaving: "Saving...",
    invalidTitle: "Invalid or expired link",
    invalidText: "This link was already used or is older than 60 minutes. Request a new one to continue.",
    requestNew: "Request a new link",
    doneTitle: "Password changed",
    doneText: "For your security, you were signed out of all devices. Sign in with the new password.",
    verifyingTitle: "Confirming your email...",
    verifiedTitle: "Email confirmed",
    verifiedText: "Your account is active. Sign in to answer the interview and get your training plan.",
    verifyFailTitle: "We could not confirm it",
    verifyFailText: "The link is invalid or expired (valid for 24 hours). Enter your email and we will send another.",
    resend: "Resend confirmation email",
    resending: "Sending...",
    resendDone: "If an account is waiting for confirmation with that email, we sent another link.",
    spamNote: "Can't find the email? Check your spam or junk folder too.",
  },
  validation: {
    email: "Enter a valid email.",
    password: "The password needs at least 10 characters.",
    passwordRequired: "Enter your password.",
    name: "Enter your name.",
    required: "Required to create the account.",
    mismatch: "The passwords don't match.",
    pw: {
      TOO_SHORT: "The password needs at least 10 characters.",
      TOO_LONG: "The password is too long.",
      COMMON: "That password is too common. Choose another.",
      CONTAINS_EMAIL: "The password can't contain parts of your email.",
      TOO_SIMPLE: "The password is too simple: mix more different characters.",
    },
  },
  errors: {
    generic: "Something went wrong. Please try again shortly.",
    INVALID_TOKEN: "Invalid or expired link.",
    network: "Can't reach the server. Check your internet connection.",
    serverDown: "The server did not respond. Try again in a moment.",
    INVALID_CREDENTIALS: "Wrong email or password.",
    EMAIL_NOT_VERIFIED: "Confirm your email before signing in.",
    RATE_LIMITED: "Too many attempts. Wait a moment and try again.",
    VALIDATION_ERROR: "Check the information you entered.",
    WEAK_PASSWORD: "Choose a stronger password: avoid common passwords and parts of your email.",
    ACCOUNT_LOCKED: "Account locked for a few minutes after several attempts. Try again later.",
    UNDERAGE: "Let's Go Run is only for people aged 18 or over.",
    INTERVIEW_INCOMPLETE: "Some interview steps are missing.",
    UNAUTHORIZED: "Your session ended. Please sign in again.",
  },
  interview: {
    title: "Your interview",
    stepOf: "Step {{current}} of {{total}}",
    saveNote: "Your answers are saved at every step. You can leave and continue later.",
    progress: "Interview progress",
    back: "Back",
    next: "Continue",
    saving: "Saving...",
    finish: "Finish interview",
    finishing: "Building your plan...",
    yes: "Yes",
    no: "No",
    loadError: "We could not load the interview.",
    retry: "Try again",
    errors: {
      required: "Please answer this field.",
      invalid: "Please check this value.",
    },
    underage: {
      title: "Let's Go Run is only for people aged 18 or over",
      text: "Under the app's rules we cannot build a plan for anyone under 18. Your data will not be used. If you want, you can delete your account now.",
      password: "Your password, to confirm the deletion",
      delete: "Delete my account",
      deleting: "Deleting...",
    },
    steps: {
      basicos: {
        name: "Basics",
        title: "Let's start with you",
        text: "A few details to work out your level and protect your health.",
      },
      triagem: {
        name: "Health",
        title: "Health screening",
        text: "This is the most important safety step. Please answer honestly.",
      },
      historico: {
        name: "Running",
        title: "Your running so far",
        text: "We use this to choose the starting point of your plan. When in doubt, give the more conservative value.",
      },
      lesoes: {
        name: "Injuries",
        title: "Injuries and pain",
        text: "Pain and injury change what is safe to train. The app does not diagnose: if you have pain, see a health professional.",
      },
      objetivo: {
        name: "Goal",
        title: "Your goal",
        text: "What do you want from running? Your plan is built from this.",
      },
      disponibilidade: {
        name: "Time",
        title: "Your availability",
        text: "We only schedule workouts on the days and for the time you give us.",
      },
      recuperacao: {
        name: "Recovery",
        title: "Recovery and routine",
        text: "Sleep, stress and routine also count towards the load your body can handle.",
      },
      recursos: {
        name: "Resources",
        title: "What you have at hand",
        text: "Last step. This only helps fit the workouts to your daily life.",
      },
    },
    basicos: {
      birthMonth: "Month of birth",
      birthMonthPlaceholder: "Choose the month",
      birthYear: "Year of birth",
      birthYearHint: "Month and year only: we keep the minimum necessary.",
      sex: "Sex",
      sexM: "Male",
      sexF: "Female",
      sexNone: "Prefer not to say",
      heightCm: "Height (cm)",
      weightKg: "Weight (kg) — optional",
    },
    triagem: {
      intro: "About this questionnaire",
      note: "This is the official PAR-Q+. You do not need to write your conditions or medications: we only keep the Yes and No answers. This is not a diagnosis and does not replace a professional assessment.",
      english: "The PAR-Q+ is shown in its original English version, published by the PAR-Q+ Collaboration, without changes.",
      pregnant: "Are you pregnant right now?",
      pregnantHint: "App question, outside the PAR-Q+. If pregnant, training is postponed until a health professional gives guidance.",
      attribution: "Source",
    },
    historico: {
      monthsRegular: "For how many months have you been running regularly?",
      monthsRegularHint: "At least once a week, without long breaks. If you do not run yet, enter 0.",
      weeklySessions: "How many times a week did you run, on average, in the last 4 weeks?",
      weeklySessionsHint: "Decimals are fine, such as 2.5. If you did not run, enter 0.",
      volumeUnit: "How do you prefer to enter your weekly volume?",
      unitKm: "In kilometers",
      unitMin: "In minutes",
      volumeKm: "Average weekly volume (km) over the last 4 weeks",
      volumeMin: "Average weekly volume (minutes) over the last 4 weeks",
      longestRecentKm: "Longest distance you ran in one go, recently (km)",
      longestRecentKmHint: "If you did not run, enter 0.",
      canRunMinutes: "For how many minutes can you run without stopping?",
      canRunMinutesHint: "At a comfortable pace, without walking. If you cannot yet, enter 0.",
      daysSinceLastRun: "How many days ago was your last run?",
      daysSinceLastRunHint: "Today is 0.",
      half: "Did you complete a half marathon (21 km) in the last 3 months?",
      regular: "In the last 3 months, did you do planned exercise of at least 30 minutes, at moderate intensity, on 3 or more days a week?",
      timesTitle: "Recent times (optional)",
      timesHint: "If you ran a race or a test recently, enter the time. Leave blank if you do not have one.",
      t5k: "5 km time",
      t10k: "10 km time",
      minutes: "Minutes",
      seconds: "Seconds",
      date: "Date",
    },
    lesoes: {
      injury12m: "Did you have any injury in the last 12 months?",
      runningInjury3m: "Did you have an injury caused by running in the last 3 months?",
      persistent6w: "Did you have an injury or persistent pain in the last 6 weeks?",
      painNow: "How intense is the pain you feel today?",
      painScale: "0 = no pain; 10 = the worst pain possible.",
      painPart: "Where does it hurt? (optional)",
      painPartHint: "For example: right knee, calf.",
    },
    objetivo: {
      goal: "What is your goal?",
      goals: {
        saude: "Health and well-being",
        emagrecimento: "Weight loss",
        primeira_prova: "My first race",
        melhorar_tempo: "Improve my time",
      },
      raceDistance: "What is the race distance?",
      raceDate: "Race date (optional)",
      raceDateHint: "If you have not chosen one yet, leave it blank.",
      timeDistance: "Which distance do you want to improve your time at?",
      timeDate: "Do you have a race scheduled? Enter the date (optional)",
      timeDateHint: "If you don't, leave it blank.",
    },
    disponibilidade: {
      daysPerWeek: "On how many days a week can you train?",
      minutesPerSession: "How much time do you have per workout (minutes)?",
      minutesHint: "Between 10 and 300 minutes.",
      longRunDay: "Which day is best for the longest workout?",
      longRunPlaceholder: "Choose the day",
    },
    recuperacao: {
      sleepHours: "How many hours do you sleep per night, on average?",
      sleepHint: "Between 3 and 12 hours. Decimals are fine, such as 7.5.",
      stress: "How is your stress level day to day?",
      stressLevels: {
        "1": "Very low",
        "2": "Low",
        "3": "Medium",
        "4": "High",
        "5": "Very high",
      },
      workType: "What is your job like?",
      work: {
        sedentario: "Sedentary (mostly sitting)",
        misto: "Mixed (standing and sitting)",
        fisico: "Physical (effort during the day)",
      },
      otherSports: "How many minutes a week do you do other sports?",
      otherSportsHint: "Add it all up, not counting running. If none, enter 0.",
      strength: "Do you do strength training (gym or similar)?",
    },
    recursos: {
      hrDevice: "Do you use a device to measure heart rate?",
      hr: { nenhum: "None", relogio: "Watch", cinta: "Chest strap" },
      track: "Do you have access to a running track?",
      treadmill: "Do you have access to a treadmill?",
      terrain: "What terrain do you usually run on?",
      terrainHint: "Tick all that apply. You can leave it blank.",
      terrains: { plano: "Flat", subidas: "Hills", trilha: "Trail" },
    },
  },
  result: {
    title: "Your runner profile",
    intro: "See how you were classified and what it means for your plan.",
    loadError: "We could not load your profile.",
    frequencyTitle: "Frequency in the week",
    levelTitle: "Your level",
    levels: { iniciante: "Beginner", intermediario: "Intermediate", avancado: "Advanced" },
    nextEvaluation: "Next reassessment: {{date}}.",
    screeningTitle: "Health screening",
    validUntil: "The clearance is valid until {{date}}.",
    disclaimer: "This is not a diagnosis and does not replace a professional assessment.",
    officialText: "PAR-Q+ recommendations",
    officialEnglish: "The text above is the original English version of the PAR-Q+ (PAR-Q+ Collaboration, 2025), without changes.",
    states: {
      LIBERADO: { title: "Screening complete", text: "From your answers, you can start training, gradually." },
      LIBERADO_APOS_AVALIACAO: {
        title: "Cleared after assessment",
        text: "You recorded a professional assessment with no restrictions.",
      },
      AVALIACAO_NECESSARIA: {
        title: "Before starting, get more guidance",
        text: "Your answers suggest it is best to talk to a doctor or a qualified exercise professional before training. No workouts have been generated for now.",
      },
      ADIADO: {
        title: "Training postponed",
        text: "From your answers, it is best to wait before training. No workouts have been generated for now.",
      },
      EXPIRADO: {
        title: "Screening expired",
        text: "Your screening has expired or your health has changed. Redo the interview to continue.",
      },
      NAO_FEITA: { title: "Screening pending", text: "Finish the interview to see your result." },
    },
    reasons: {
      gravidez: "Pregnancy reported.",
      sintoma_geral: "You answered Yes to a symptom question (chest pain, dizziness, or advice to exercise only under supervision).",
      seguimento_sim: "You answered Yes to one of the additional health questions.",
      avaliacao_com_restricoes: "The reported assessment showed restrictions, and the app cannot yet respect them.",
      avaliacao_sem_restricoes: "After the assessment, workouts stay at light to moderate intensity unless the clearance expressly includes vigorous exercise.",
      "45_sem_exercicio_regular": "At 45 or older with no recent regular exercise, workouts start light until you see a qualified exercise professional.",
      validade_vencida: "The clearance period has ended.",
      saude_mudou: "You reported a change in your health.",
    },
    evaluation: {
      title: "Have you been assessed by a professional?",
      text: "If a doctor or qualified exercise professional assessed you, record the result to unlock workouts. We do not ask for any document.",
      eparmedx: "ePARmed-X+ (www.eparmedx.com)",
      result: "Assessment result",
      results: {
        sem_restricoes: "Cleared, no restrictions",
        com_restricoes: "Cleared, with restrictions",
        encaminhado_ao_medico: "Referred to a doctor",
        nao_liberado: "Not cleared",
      },
      date: "Assessment date",
      source: "Assessed by",
      sources: {
        eparmedx: "ePARmed-X+",
        medico: "Doctor",
        profissional_educacao_fisica: "Exercise professional",
      },
      vigorous: "The clearance expressly includes vigorous exercise.",
      submit: "Record assessment",
      saving: "Recording...",
      placeholder: "Choose",
      stillBlocked: "We recorded the assessment. With this result, workouts remain blocked.",
    },
    planTitle: "Your plan",
    planObjective: "Goal",
    objectives: { saude: "Health and well-being", "5k": "5 km race", "10k": "10 km race", "21k": "Half marathon (21 km)", "42k": "Marathon (42 km)" },
    planLevel: "Plan level",
    planLength: "{{weeks}} weeks",
    planBase: "+ {{weeks}} of base before the template",
    planDates: "{{start}} to {{end}}",
    planStart: "Start and end",
    planEntry: "Where you start",
    placement: {
      declarado: "Stage {{etapa}} of the run-walk program, based on how long you said you can already run without stopping.",
      teto_de_volume:
        "Stage {{etapa}} of the run-walk program. You can already run longer than this without stopping, but the weekly total cannot jump all at once, so your week starts here.",
      default: "Stage {{etapa}} of the run-walk program, the beginning, at the most careful pace. You move up as you feel ready.",
    },
    planNone: "There is no plan yet. When the screening allows it, the plan is built automatically.",
    toPanel: "Go to the dashboard",
    redo: "Redo the interview",
  },
  settings: {
    title: "Profile and settings",
    link: "Settings",
    loadError: "We could not load the settings.",
    accountTitle: "Account",
    photoAdd: "Upload photo",
    photoChange: "Change photo",
    photoRemove: "Remove photo",
    photoInput: "Choose a profile photo",
    photoHint: "Optional. The photo is shrunk on your device before it is sent and only you can see it.",
    photoErrors: {
      type: "Use a JPEG, PNG or WebP image.",
      size: "The image is too large (10 MB maximum).",
      decode: "That image could not be opened. Try another one.",
      too_big: "That image could not be reduced enough. Try another one.",
    },
    nameSave: "Save name",
    nameSaved: "Name saved.",
    emailChange: "Change email",
    emailExplain:
      "We send a link to the new address. The account email only changes when you open that link; after that you will need to sign in again.",
    newEmail: "New email",
    currentPassword: "Your current password",
    emailSend: "Send confirmation link",
    emailSent: "If {{email}} can receive messages, we sent the confirmation link. The email only changes after you open it.",
    cancel: "Cancel",
    name: "Name",
    email: "Email",
    language: "App language",
    devicesTitle: "Connected devices",
    devicesText: "If you do not recognize a device, end its session.",
    thisDevice: "This device",
    lastSeen: "Last seen: {{date}}",
    endSession: "End session",
    unknownDevice: "Unknown device",
    healthTitle: "Health and training",
    screeningLink: "See health screening and plan",
    nextEvaluation: "Next reassessment: {{date}}.",
    redoTitle: "Redo the interview",
    redoText: "Recalculates your level and builds a new plan. The current plan is ended. Use it when your routine, health or goal change.",
    redoCta: "Redo the interview",
    pauseTitle: "Pause training",
    pauseText: "If you have pain, an injury or an illness, pause. Running only returns after you record a health professional's clearance, and the return is gradual.",
    pauseCause: "What happened?",
    causes: { dor: "Pain", lesao: "Injury", doenca: "Illness" },
    pauseCta: "Pause training",
    pausing: "Pausing...",
    pauseActive: "Training paused for {{cause}} since {{date}}.",
    pauseDone: "Clearance recorded on {{date}}. The return is gradual.",
    clearanceTitle: "Record professional clearance",
    clearanceText: "If a doctor or qualified health professional assessed you and cleared you to run again, record the date. We do not ask for any document.",
    clearanceDate: "Assessment date",
    clearanceCta: "Record clearance",
    clearanceSaving: "Recording...",
    dataTitle: "Your data",
    dataText: "Your health data is sensitive. You can download a copy or erase everything whenever you want.",
    exportCta: "Download my data",
    exporting: "Preparing...",
    exportError: "We could not download the data.",
    deleteTitle: "Delete my account",
    deleteText: "Truly erases your account and all your data: interview, screening, plan, check-ins, workouts and pain. It cannot be undone. Download a copy first if you want.",
    deleteOpen: "Delete my account",
    deletePassword: "Your password, to confirm",
    deleteSure: "I understand this cannot be undone.",
    deleteCta: "Delete everything now",
    deleting: "Deleting...",
    deleteCancel: "Cancel",
  },
  lighter: {
    text: "Not 100% today, but no pain or discomfort? You can ask for a lighter workout.",
    cta: "I'm not 100% today",
    onText: "You asked for a lighter day: today's workout is 20% shorter and has no hard parts.",
    undo: "Undo and go back to the plan's workout",
  },
  pause: {
    title: "Planned break",
    tag: "Planned break",
    text: "Traveling or taking a few days off? Set the break in advance. Those days have no workout or reminder, and they don't count as missed workouts. Breaks for pain, injury or illness are in Settings.",
    start: "First day without training",
    end: "Last day without training",
    save: "Set break",
    update: "Save new dates",
    cancel: "Cancel the break",
    end_now: "End the break today",
    saved: "Break saved.",
    current: "Break from {{start}} to {{end}}.",
    started: "You are on a break until {{end}}. Only the return date can change.",
    noticeUpcoming: "Planned break from {{start}} to {{end}}.",
    noticeActive: "You are on a planned break until {{end}}.",
    noticeLink: "See or change the break",
    returnTitle: "How the return works",
    returnNote: "Assuming you train until the day before the break. If you stop earlier, the return follows the real days without running.",
    return: {
      segue: "The plan goes on as usual.",
      congela: "The plan repeats last week's volume before going up again.",
      reduzido: "You come back at 70% of the volume and with no very hard workouts for 2 weeks.",
      longa: "You come back at half the volume (or the start of your level, if lower), with no very hard workouts for 2 weeks. If your health screening has expired, the app asks you to redo it.",
    },
    errors: {
      date_in_past: "Choose today or a future date.",
      after_plan_end: "The break can't go past the end of your plan.",
      pause_started: "The break has already started: only the return date can change.",
      no_plan: "You don't have a plan yet.",
      order: "The last day comes after the first.",
    },
  },
  move: {
    title: "Change the day",
    text: "Can't train on this day? You can move this workout to another day this week.",
    cta: "Pick another day",
    pick: "Which day?",
    cancel: "Cancel",
    rules: "Only the days that keep the rest your plan needs are shown: hard workouts never on back-to-back days and, early on, a free day between runs.",
    none: "There is no other day this week that keeps the rest between workouts. If you can't train, mark it as not done.",
    done: "Done. The workout moved to {{date}}.",
    blocked: {
      generic: "The workout can't be moved to that day.",
      dia_ocupado: "That day already has another workout.",
      corridas_seguidas: "That would put two runs on back-to-back days, and your plan still needs a free day between them.",
      qualidade_seguida: "That would put two hard workouts on back-to-back days.",
      forca_com_sessao_dura: "Strength training doesn't go on the same day as a hard run.",
      forca_sem_intervalo: "Strength sessions need two days between them.",
      fora_da_semana: "You can only move it within this plan week.",
      dia_passado: "You can only move it to today or later.",
      ja_registrado: "This workout has already been logged.",
    },
  },
  workout: {
    watch: "If you use a heart rate watch",
    studiesTitle: "What the studies used",
    sourcesTitle: "Scientific basis for this advice",
    back: "Back to the dashboard",
    notFound: "We could not find this workout.",
    loadError: "We could not load the workout.",
    optional: "Complementary",
    ladderTitle: "Stage {{etapa}} of the ladder",
    ladderRepeat: "{{reps}} times: {{run}} min running and {{walk}} min walking",
    ladderContinuous: "Continuous run of {{run}} min",
    todayTitle: "Why today looks like this",
    reasonTitle: "Why this workout",
    howTitle: "How to do it",
    warmup: "Warm-up",
    main: "Main part",
    cooldown: "Cool-down",
    intensityTitle: "Intensity",
    levelTitle: "For your level",
    limitsTitle: "Limits and care",
    limitsNote: "The app does not diagnose. If pain does not go away, or you feel dizzy or unusually short of breath, stop and see a health professional.",
    nextTitle: "Progression and regression",
    progression: "To progress",
    regression: "To ease off",
    evidenceTitle: "Scientific basis",
    evidenceDirect: "What the research supports",
    evidenceProvisional: "Initial values used by the app",
    evidenceProvisionalHint: "These are sensible starting points, still without direct studies, and may be recalibrated.",
    evidenceInsufficient: "Not enough evidence",
    referencesTitle: "Sources",
    openSource: "Open source",
    status: { completed: "Done", skipped: "Not done", pending: "Planned" },
  },
  glossary: {
    title: "Glossary",
    note: "Plain definitions to help you read the workout.",
    terms: {
      vo2max: { term: "VO2max", text: "The maximum amount of oxygen the body can use per minute during hard effort. It is the most common measure of aerobic capacity." },
      threshold: { term: "Threshold", text: "The effort level at which the body's response changes; for example, when breathing starts to speed up. The app uses two thresholds to separate the three zones (easy, moderate and hard). There are many threshold concepts and they differ from each other (Faude, 2009), so the app describes threshold as an effort range rather than a single pace." },
      economy: { term: "Running economy", text: "How much energy (oxygen) you spend to run at a given speed. The better the economy, the less effort the body makes for the same pace." },
      maxHr: { term: "Maximum heart rate", text: "The highest number of beats per minute your heart reaches at maximal effort. It can be measured (in an exercise test) or estimated by a formula. The estimate is off by about 10 beats per minute on average, so ranges in % of it are only approximate." },
      oneRm: { term: "1RM (one-repetition maximum)", text: "The heaviest load you can lift for a single repetition with good technique. Strength loads are given as a percentage of it." },
      effort: { term: "Perceived effort (0 to 10)", text: "A score you give the effort of the workout, from 0 to 10: 3 is moderate, 5 is hard and 7 is very hard. It is Foster's CR-10 scale." },
      talkTest: { term: "Talk test", text: "Say a sentence out loud while running. If you can do it without stopping to breathe, the effort is easy (Z1); if you can only manage short sentences, it is moderate (Z2); if you cannot, it is hard (Z3)." },
    },
  },
  history: {
    title: "History",
    link: "History",
    loadError: "We could not load your history.",
    periodLabel: "Period",
    period: "{{weeks}} weeks",
    summaryDone: "Workouts done",
    summaryDoneValue: "{{done}} of {{total}}",
    summaryMinutes: "Training minutes",
    summaryPace: "Average pace",
    summarySleep: "Average sleep",
    summarySleepValue: "{{hours}} h",
    summaryNone: "—",
    adherenceTitle: "Workouts done per week",
    adherenceHint: "Workouts done out of those planned up to today. Complementary sessions are left out.",
    adherenceReadout: "Week of {{date}}: {{done}} of {{total}} workouts ({{pct}}%).",
    adherenceEmpty: "Week of {{date}}: no workouts planned yet.",
    loadTitle: "Weekly load",
    loadHint: "Perceived effort (0 to 10) times the minutes of each workout done, summed over the week. Use it to compare one week with another; it is not a target.",
    loadReadout: "Week of {{date}}: load {{load}} over {{minutes}} min.",
    loadUnit: "units",
    sleepTitle: "Sleep",
    sleepHint: "Hours slept as reported in each day's check-in.",
    sleepReadout: "{{date}}: {{hours}} h of sleep.",
    sleepReadoutQuality: "{{date}}: {{hours}} h of sleep, quality {{quality}} of 5.",
    sleepNoData: "{{date}}: no check-in with sleep hours.",
    painTitle: "Pain reported",
    painEmpty: "No pain reported in this period.",
    painIntensity: "intensity {{value}} of 10",
    moments: { treino: "during the workout", manha_seguinte: "the next morning", checkin: "at check-in" },
    emptyTitle: "No history yet",
    emptyText: "Do your check-in and log your workouts: the charts show up here.",
    emptyCta: "Go to the dashboard",
    showTable: "View as table",
    tableWeek: "Week",
    tableDone: "Done",
    tablePlanned: "Planned",
    tableAdherence: "Adherence",
    tableMinutes: "Minutes",
    tableLoad: "Load",
    tablePace: "Pace (min/km)",
    tableDate: "Date",
    tableHours: "Hours",
    tableQuality: "Quality",
    painNote: "Pain records help you spot patterns, but they are not a diagnosis. If pain persists or gets worse, see a health professional.",
  },
  legal: {
    updated: "Last updated: {{date}}",
    version: "version {{version}}",
    toc: "On this page",
    back: "Back",
    enNotice: "This is a translation of the Portuguese version. If there is any difference between the two, the Portuguese version prevails.",
  },
  emailChange: {
    confirmingTitle: "Confirming your new email...",
    doneTitle: "Email changed",
    doneText: "Your new email is now in effect. For security, sessions were signed out: sign in again with the new address.",
    failTitle: "Invalid or expired link",
    failText: "This link is no longer valid. Request the email change again in Settings.",
  },
  review: {
    title: "How was this 2-week block?",
    text: "Your answer helps take better care of you: it can make the next workouts calmer, never heavier.",
    options: {
      facil: "Easy, I had energy left",
      no_ponto: "Just right",
      puxado: "Hard, I got more tired than expected",
      nao_consegui: "I could not keep up",
    },
    saved: "Answer saved.",
    effect: {
      facil: "Great! The plan continues the same way, without skipping steps. If you want more, you can re-evaluate your level.",
      no_ponto: "Perfect, the plan continues.",
      puxado: "Let's take it easy: in run-walk, next week repeats this step. If you feel pain, log it and see a professional.",
      nao_consegui: "Let's take it easy: in run-walk, next week repeats this step. If you feel pain, log it and see a professional.",
    },
    suggestTitle: "Want a slightly more advanced plan?",
    suggestText:
      "You said the last block was easy. Your current plan continues without skipping steps, but you can redo the assessment: if your fitness allows, the app suggests a starting point further ahead.",
    suggestCta: "Redo the assessment",
    completedTitle: "You completed your {{objective}} plan",
    completedText:
      "Congratulations on the work! To keep going, redo the assessment and choose your next goal. The app recalculates your level with what you can do today.",
    completedCta: "Choose the next goal",
  },
  nav: {
    label: "Main navigation",
    menu: "Menu",
    account: "Account",
    panel: "Dashboard",
    plan: "Training plan",
    history: "History",
    profile: "Profile and plan",
    settings: "Settings",
    level: "{{level}} level",
    openMenu: "Open menu",
    closeMenu: "Close menu",
  },
  dash: {
    greetMorning: "Good morning, {{name}}",
    greetAfternoon: "Good afternoon, {{name}}",
    greetEvening: "Good evening, {{name}}",
    subtitle: "{{date}} · Week {{week}} of {{total}} · {{objective}}",
    subtitleShort: "{{date}} · Week {{week}}",
    stats: {
      distance: "Distance this week",
      distanceHint: "Sum of workouts with a distance entered",
      distanceNone: "Enter the distance when you log a workout",
      time: "Training time",
      timeHint: "{{done}} of {{total}} workouts done",
      pace: "Average pace",
      paceHint: "From workouts with distance and duration",
      paceNone: "Shows up when you enter distance and duration",
      sleep: "Average sleep",
      sleepHint: "Over the last 7 days",
      sleepNone: "Shows up with your daily check-ins",
    },
    today: { duration: "Duration", zone: "Zone", effort: "Target effort", effortValue: "{{rpe}} out of 10", zoneShort: "{{level}} ({{zone}})" },
    volume: {
      title: "Weekly volume",
      done: "Done",
      planned: "Planned",
      minutesDone: "Done",
      minutesLeft: "Left",
      sessions: "Workouts done",
      distance: "Distance",
      chartLabel: "Training minutes per day of the week",
      readout: "{{day}}: {{done}} min done of {{planned}} min planned",
      readoutPlanned: "{{day}}: {{planned}} min planned",
      readoutRest: "{{day}}: no workout",
    },
    weekPlan: { title: "This week's plan", seeAll: "See full plan →", rest: "Rest", done: "Done", today: "Today", skipped: "Not done", pending: "Planned" },
    kinds: {
      rodagem: "Easy",
      qualidade: "Quality",
      longao: "Long run",
      regenerativo: "Recovery",
      "caminhada-corrida": "Ladder",
      forca: "Strength",
      mobilidade: "Mobility",
    },
    goal: { daysLeft: "days", weeksLeft: "weeks", currentWeek: "current week", progress: "Plan progress" },
    recent: { title: "Recent workouts", seeAll: "See history", empty: "The workouts you log this week show up here." },
  },
  healthChange: {
    title: "Has anything changed in your health?",
    text: "For example: a new diagnosis, a new medication, an injury, a pregnancy or a hospital stay. We ask every week because your clearance to train is only valid while your health is as it was in the screening.",
    no: "Nothing changed",
    yes: "Yes, something changed",
    thanks: "Thank you for your answer.",
    thanksText: "We will ask again in a week.",
    confirmTitle: "Let's redo the health screening",
    confirmText: "Your workouts stay on hold until you redo the health questionnaire (PAR-Q+). This is only so the app can clear you again safely; it is not a diagnosis. If it is urgent, see a health professional.",
    confirmCta: "Confirm and continue",
    saving: "Saving...",
    back: "Back",
  },
  plan: {
    title: "Full plan",
    link: "Plan",
    loadError: "We could not load your plan.",
    goalLine: "{{objective}} · {{level}}",
    dates: "From {{start}} to {{end}}",
    tileWeek: "Current week",
    tileWeekValue: "{{n}} of {{total}}",
    tileFinished: "Plan completed",
    tileNotStarted: "Starts on {{date}}",
    tileDone: "Workouts done so far",
    tileDoneValue: "{{done}} of {{total}}",
    tileEnds: "Ends on",
    note: "The weeks ahead show only the outline of the plan. The workouts of each week are built when that week starts, based on what you have done by then.",
    noteLadder: "On the walk-run ladder, a repeated step pushes the following weeks back. That is why the order of the weeks ahead is a forecast.",
    weeksTitle: "Week by week",
    current: "Current week",
    projected: "Forecast",
    noSessions: "No workouts this week.",
  },
  news: {
    bannerTitle: "App news",
    understood: "Got it",
    seeAll: "See all news",
    pageTitle: "News",
    pageIntro: "Bug fixes, app limitations and notices worth knowing.",
    empty: "There is no news for you yet.",
    kind: { correcao: "Fix", limitacao: "App limitation", novidade: "News" },
  },
  planNotice: {
    title: "Your race is too close for this plan",
    needs_one: "This distance needs about {{count}} week of preparation.",
    needs_other: "This distance needs about {{count}} weeks of preparation.",
    until_one: "There is {{count}} week until the race.",
    until_other: "There are {{count}} weeks until the race.",
    untilNone: "There are not enough weeks until the race.",
    safety: "For your safety, the app never shortens a plan.",
    choose: "Choose a date from {{date}}.",
    chooseWithDistance: "Choose a date from {{date}} or a shorter distance: {{distances}}.",
    or: " or ",
    distances: { "5k": "5 km", "10k": "10 km", "21k": "21 km" },
    cta: "Redo the interview with another date",
  },
  pace: { value: "{{pace}} /km" },
  panel: {
    title: "Dashboard",
    planCta: "Full plan",
    welcome: "Welcome, {{name}}!",
    hello: "Hello, {{name}}",
    seeProfile: "See my profile and plan",
    loadError: "We could not load your dashboard.",
    todayTitle: "Today's workout",
    weekTitle: "Your week",
    noPlanTitle: "There is no active plan yet",
    noPlanText: "Your plan has not been built yet. See the screening result to understand why and what to do next.",
    actions: {
      manter: "Workout kept",
      reduzir: "Workout reduced",
      regenerativo: "Recovery workout",
      descanso_ou_mobilidade: "Rest or mobility",
      interromper_e_encaminhar: "Stop and see a professional",
    },
    rest: "Rest today",
    restPlanned: "Planned rest",
    logCta: "Log workout",
    logEdit: "Edit workout log",
    detailsCta: "See the full workout",
    restText: "There is no run on your schedule today.",
    minutes: "{{count}} min",
    zone: "Up to {{level}} effort ({{zone}})",
    zoneNames: { Z1: "easy", Z2: "moderate", Z3: "hard" },
    rpe: "Target effort: {{rpe}} out of 10",
    whyTitle: "Why today looks like this",
    complementary: "Today's complementary work",
    notAllowedToday: "Not recommended today",
    checkinTitle: "Do today's check-in",
    checkinText: "It takes less than 30 seconds and helps adjust the day's workout.",
    checkinDone: "Today's check-in is done.",
    checkinDoneText: "Today's answers are saved. If something changed, you can edit them.",
    checkinCta: "Do check-in",
    checkinEdit: "Edit check-in",
    urgent: "In an emergency (chest pain that does not go away, fainting or severe shortness of breath), call 192 (SAMU).",
    weekNumber: "Week {{n}}",
    phases: { base: "Base", construcao: "Build", especifica: "Specific", polimento: "Taper", prova: "Race" },
    recoveryWeek: "Recovery week",
    taperWeek: "Taper week",
    raceWeek: "Race week",
    weekDone: "{{done}} of {{total}} workouts done",
    weekMinutes: "{{done}} of {{total}} min",
    todayTag: "today",
    status: { completed: "Done", skipped: "Not done", pending: "Planned" },
    noSessions: "No workouts planned this week.",
    optional: "optional",
    goalTitle: "Your goal",
    goalPlanEnds: "The plan ends on {{date}}.",
  },
  body: {
    view: "Body view",
    front: "Front",
    back: "Back",
    regions: {
      lombar_sacro: { x: "Lower back / sacrum" },
      quadril_virilha: { d: "Right hip / groin", e: "Left hip / groin" },
      gluteo: { d: "Right glute", e: "Left glute" },
      coxa_frente: { d: "Right thigh (front)", e: "Left thigh (front)" },
      coxa_atras: { d: "Right thigh (back)", e: "Left thigh (back)" },
      joelho: { d: "Right knee", e: "Left knee" },
      canela: { d: "Right shin", e: "Left shin" },
      panturrilha: { d: "Right calf", e: "Left calf" },
      tendao_aquiles: { d: "Right Achilles tendon", e: "Left Achilles tendon" },
      tornozelo: { d: "Right ankle", e: "Left ankle" },
      pe_dorso: { d: "Right foot (top)", e: "Left foot (top)" },
      pe_lateral: { d: "Right foot (outer side)", e: "Left foot (outer side)" },
      pe_planta_calcanhar: { d: "Right foot (sole and heel)", e: "Left foot (sole and heel)" },
    },
  },
  log: {
    kicker: "Log workout",
    didIt: "Did you do this workout?",
    duration: "Actual duration (minutes)",
    durationHint: "Include warm-up, walking breaks and intervals.",
    plannedTime_one: "The planned time is logged: {{count}} minute.",
    plannedTime_other: "The planned time is logged: {{count}} minutes.",
    paceLive: "Average pace for this workout: {{pace}} per km. It is only a reference, not a target.",
    distance: "Distance (km) — optional",
    srpe: "How hard was the whole workout?",
    srpeScale: "1 very very easy · 2 easy · 3 moderate · 4 somewhat hard · 5 hard · 7 very hard · 10 maximal. Numbers 6, 8 and 9 sit between the labels.",
    painTitle: "Pain during the workout",
    painHelp: "Tap the spot on the map. You can mark more than one. If you had no pain, leave it blank.",
    noPain: "No spot marked.",
    painIntensityMissing: "Enter the pain intensity for each marked spot.",
    invalid: "Please check the pain details.",
    intensity: "How intense is the pain?",
    intensityScale: "1 = very mild · 10 = the worst pain possible.",
    type: "What is the pain like?",
    types: { pontada: "Sharp / stabbing", difusa: "Diffuse", outra: "Other" },
    flagsTitle: "Tick what applies to this spot",
    flags: {
      suddenOnset: "It started suddenly during the workout",
      changedStride: "The pain changed my stride (limping, compensating, avoiding one side)",
      worsensEachStrideOrNoWarmup: "It gets worse with every stride or does not ease with warm-up",
      bonePoint: "It hurts at a point on the bone (to the touch or when bearing weight)",
      restOrNightPain: "It hurts at rest or at night",
      painWalking: "It hurts when walking",
      jointSwelling: "There is visible swelling in the joint",
      lockingOrGivingWay: "The joint locks, catches or gives way",
      tinglingOrNumbness: "There is tingling or numbness",
      suddenWeaknessOrFootDrop: "I suddenly lost strength or my foot dropped",
      darkUrineWithMuscleSwelling: "Dark (tea-colored) urine with intense muscle pain or swelling",
      analgesicBeforeTraining: "I took a painkiller or anti-inflammatory before the workout",
    },
    removeRegion: "Remove",
    symptomsTitle: "Symptoms during or after the workout",
    symptomsHelp: "Tick any you felt. If none, leave blank.",
    cardio: {
      chestPain: "Chest pain or tightness",
      disproportionateBreathlessness: "Shortness of breath out of proportion to the effort",
      dizzyLosingBalance: "Dizziness with loss of balance",
      fainting: "Fainting or near-fainting",
      palpitations: "Irregular palpitations",
    },
    heat: {
      confusionOrCollapse: "Confusion, disorientation or collapse",
      headacheNauseaInHeat: "Headache, dizziness, nausea or vomiting in the heat",
    },
    notes: "Comment (optional)",
    submit: "Save log",
    notSaved: "Not saved yet. Still to answer:",
    distanceInvalid: "Distance (numbers only, like 5.2)",
    saving: "Saving...",
    cancel: "Cancel",
    notFound: "We could not find this workout.",
    backToPanel: "Back to dashboard",
    doneTitle: "Log saved",
    nextDayTitle: "What changes for tomorrow",
    referNote: "From what you logged, see a health professional before running again. The app does not diagnose.",
    disclaimer: "The app does not diagnose: if the pain or symptom does not go away, see a health professional.",
  },
  reminder: {
    title: "Workout reminder",
    text: "A notice on this device when you have a workout.",
    day: "When to notify",
    days: { mesmo_dia: "On the workout day", vespera: "The day before" },
    time: "Time",
    hour: "Hour",
    minute: "Minutes",
    turnOn: "Turn on reminder",
    save: "Save",
    off: "Turn off",
    on: { mesmo_dia: "On: on the workout day, at {{time}}.", vespera: "On: the day before the workout, at {{time}}." },
    saved: "Reminder saved.",
    blocked: "Notifications are blocked for this site. To get the reminder, allow them in your browser settings.",
    unsupported: "This browser does not receive notifications.",
    iosInstall: "On iPhone and iPad, the reminder only works with the app installed on the home screen (see Install on your phone).",
    privacy: "The notice has no health data and goes through the browser's notification service.",
  },
  install: {
    title: "Install on your phone",
    text: "Install Let's Go Run on your home screen to open it like an app, without the browser bar.",
    button: "Install the app",
    ios: "On iPhone or iPad: tap Share in Safari, then Add to Home Screen.",
    other: "In your browser menu, look for Install or Add to home screen.",
    installed: "The app is already installed on this device.",
    offline: "Without internet the app opens, but it only shows and saves your data when the connection is back.",
    offlineNotice: "No internet. Nothing is saved until the connection is back.",
  },
  weekSummary: {
    title: "How week {{n}} went",
    subtitle: "A summary of what you logged.",
    sessions: "Workouts done",
    sessionsValue: "{{done}} of {{total}}",
    time: "Training time",
    distance: "Distance",
    km: "{{km}} km",
    pace: "Average pace",
    paceValue: "{{pace}} /km",
    sleep: "Average sleep",
    hours: "{{hours}} h",
    pain: "Pain",
    painDays_one: "{{count}} day",
    painDays_other: "{{count}} days",
    painNone: "None logged",
  },
  quickLog: {
    open: "Log: {{name}}",
    openLogged: "See the log: {{name}}",
    did: "Done",
    didNot: "Not done",
    full: "Open the full log",
    markedDone: "You did this workout.",
    markedSkipped: "You did not do this workout.",
    unwell: "Did you feel any pain or feel unwell?",
    unwellSkipped: "Did you skip it because of pain or feeling unwell?",
    unwellHint: "For example: pain anywhere, chest tightness, dizziness, shortness of breath or feeling unwell in the heat.",
    goFull: "Tell us the details in the full log. There you mark where it hurt and what you felt.",
    continueFull: "Continue in the full log",
    wasDone: "You marked this workout as done.",
    wasSkipped: "You marked this workout as not done.",
    editHint: "To change it, open the log. It already has what you reported, including pain.",
    missing_one: "{{count}} workout this week has no log yet. Tap the circle to say whether you did it.",
    missing_other: "{{count}} workouts this week have no log yet. Tap the circle to say whether you did them.",
    back: "Back",
    close: "Close",
  },
  checkin: {
    title: "Today's check-in",
    text: "Less than 30 seconds. Skip anything you do not know.",
    sleepHours: "How many hours did you sleep last night?",
    sleepHint: "Decimals are fine, such as 7.5.",
    sleepQuality: "How was your sleep quality?",
    energy: "How is your energy?",
    mood: "How is your mood?",
    stress: "How is your stress today?",
    soreness: "How do your muscles feel?",
    scale: "1 = {{low}} · 5 = {{high}}",
    low: { sleepQuality: "Terrible", energy: "No energy", mood: "Bad", stress: "Very stressed", soreness: "Very sore" },
    high: { sleepQuality: "Great", energy: "Full of energy", mood: "Great", stress: "Relaxed", soreness: "No soreness" },
    restingHr: "Resting heart rate (optional)",
    restingHrHint: "In beats per minute, right after waking up.",
    illness: "Do you have a fever or a cold today?",
    painWalking: "How intense is the pain when you walk in daily life?",
    painWalkingHint: "0 = no pain; 10 = the worst pain possible.",
    submit: "Save check-in",
    saving: "Saving...",
    back: "Back to dashboard",
    errors: "Please check the highlighted values.",
  },
};
